import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import { companyInfo } from "@/mocks/company";

export default function Company() {
  const tableRows = [
    { label: "商　号", value: companyInfo.name },
    { label: "所 在 地", value: companyInfo.address },
    { label: "創　立", value: companyInfo.founded },
    { label: "資 本 金", value: companyInfo.capital },
    { label: "事業内容", value: companyInfo.business.join("\n"), multiline: true },
    { label: "従 業 員", value: companyInfo.employees },
    { label: "主要取引銀行", value: companyInfo.bank },
    { label: "適格請求書事業者登録番号", value: companyInfo.invoiceNumber },
    { label: "加 盟 協 会", value: companyInfo.associations.join("、"), multiline: true },
  ];

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="relative py-14 md:py-20 bg-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/606a33e4-7852-4064-ba13-04608c903eaa_compressed_IMG_5503.webp"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">
                    ホーム
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li className="text-background-100">会社案内</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-background-50">会社案内</h1>
            <p className="text-accent-300 text-sm mt-2">company information</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-4xl mx-auto space-y-16 md:space-y-20">
          {/* Greeting */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1.5 h-8 rounded-full bg-primary-500" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">ごあいさつ</h2>
            </div>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <p className="text-sm md:text-base text-foreground-700 leading-loose whitespace-pre-line">
                {companyInfo.greeting}
              </p>
            </div>
          </section>

          {/* Company Overview */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1.5 h-8 rounded-full bg-primary-500" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">会社概要</h2>
            </div>
            <div className="overflow-hidden rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <tbody>
                  {tableRows.map((row, i) => (
                    <tr
                      key={i}
                      className={`${i !== tableRows.length - 1 ? "border-b border-background-200/70" : ""}`}
                    >
                      <th className="w-32 md:w-48 px-4 md:px-6 py-3.5 md:py-4 bg-accent-100 text-accent-900 font-medium text-center align-middle">
                        {row.label}
                      </th>
                      <td className="px-4 md:px-6 py-3.5 md:py-4 text-foreground-800 bg-background-50 align-middle">
                        {row.multiline ? (
                          <div className="space-y-1">
                            {row.value.split("\n").map((line, j) => (
                              <p key={j}>{line}</p>
                            ))}
                          </div>
                        ) : (
                          row.value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Access / Map */}
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1.5 h-8 rounded-full bg-primary-500" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">アクセス</h2>
            </div>

            {/* 東京本社 */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70 mb-6">
              <h3 className="text-base font-bold text-foreground-900 mb-3">東京本社</h3>
              <p className="text-sm md:text-base text-foreground-700 mb-2">
                <i className="ri-map-pin-line text-primary-500 mr-2" />
                {companyInfo.address}
              </p>
              <p className="text-sm text-foreground-600 mb-2">
                <i className="ri-phone-line text-primary-500 mr-2" />
                <a href="tel:0337572881" className="hover:text-primary-600 transition-colors">TEL {companyInfo.tel}</a>　/　FAX {companyInfo.fax}
              </p>
              <p className="text-sm text-foreground-600 mb-4">
                <i className="ri-train-line text-primary-500 mr-2" />
                JR蒲田駅 → 池上線「久が原駅」徒歩6分
              </p>
              <div className="rounded-xl overflow-hidden border border-background-200/70">
                <iframe
                  src="https://maps.google.com/maps?q=35.5761,139.6862&z=16&output=embed"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="ファクトケイ株式会社 東京本社"
                />
              </div>
            </div>

            {/* 横浜研究室 */}
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-base font-bold text-foreground-900 mb-3">横浜研究室</h3>
              <p className="text-sm md:text-base text-foreground-700 mb-2">
                <i className="ri-map-pin-line text-secondary-500 mr-2" />
                {companyInfo.yokohamaAddress}
              </p>
              <p className="text-sm text-foreground-600 mb-0">
                <i className="ri-phone-line text-secondary-500 mr-2" />
                <a href="tel:0453014282" className="hover:text-secondary-600 transition-colors">TEL {companyInfo.yokohamaTel}</a>　/　FAX {companyInfo.yokohamaFax}
              </p>
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}